import { useState } from "react";
import { Button } from "V2.0/common/button";
import {
  CpuChipIcon,
  PencilIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Dialog, DialogBody, DialogTitle } from "V2.0/common/dialog";

import { GPTResponseModelSettings } from "./GPTResponseModelSettings";
import { DialogTitleWithClose } from "V2.0/components/DialogTitleWithClose";
import {
  basicModels,
  maskedModels,
  modelIcons,
  oldModels,
  premiumModels,
   previewModels,
  proModels,
  jarvisLabsModels,
  jarvisLabsProModels,
  jarvisLabsPremiumModels,
} from "./gptModels";
import { useIsIndiaResidency } from "./useIsIndiaResidency";
import { FaCrown } from "react-icons/fa";
import { Badge } from "V2.0/common/badge";
import { FaStar } from "react-icons/fa6";
import { Tooltip } from "antd";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";

const GPTResponseModels = {
  ...basicModels,
  ...oldModels,
  ...proModels,
  ...premiumModels,
  // Always known, even when not offered, so an assistant already on one displays its name
  ...jarvisLabsModels,
};

// NOTE: Catalyst's checkIsCustomModel is stricter than Frontend's (also requires
// modelName === "customLLM") — pre-existing divergence, unrelated to this port, left as-is.
function checkIsCustomModel(modelName) {
  return !Boolean(GPTResponseModels[modelName]) && modelName === "customLLM";
}

// Old: function isPremiumModel(modelName) { return Boolean(premiumModels[modelName]); }
// Jarvis Labs models live in their own tier maps (offered only for India residency)
function isPremiumModel(modelName) {
  return Boolean(premiumModels[modelName] || jarvisLabsPremiumModels[modelName]);
}

// Old: function isProModel(modelName) { return Boolean(proModels[modelName]); }
// Jarvis Labs models live in their own tier maps (offered only for India residency)
function isProModel(modelName) {
  return Boolean(proModels[modelName] || jarvisLabsProModels[modelName]);
}

function isPreviewModel(modelName) {
  return Boolean(previewModels[modelName]);
}

export function maskGptResponseModel(model) {
  return maskedModels[model] ?? model;
}

export function getModelName(model) {
  const _model = maskedModels[model] ?? model;
  return GPTResponseModels[_model] ?? _model;
}

function renderPreviewModel() {
  return (
    <Tooltip title="Occasionally inaccurate or include unnecessary text">
      <ExclamationTriangleIcon className="size-4 text-amber-500" />
    </Tooltip>
  );
}

export default function GPTResponsePremiumModel(props) {
  const {
    settings,
    syncAssistant,
    workspaceId,
    assistantId,
    showPremiumOptions,
    locked,
    audioDataResidency,
  } = props;
  const { gptResponseModel: unmaskedGptResponseModel } = settings;
  const [open, setOpen] = useState(false);
  const showIndiaDeployedModels = useIsIndiaResidency(audioDataResidency);

  const gptResponseModel = maskGptResponseModel(unmaskedGptResponseModel);
  const isCustomModel = checkIsCustomModel(gptResponseModel);
  const ModelIcon = modelIcons[gptResponseModel] ?? (() => null);

  if (isCustomModel) {
    return (
      <div className="flex items-center border border-amber-500 rounded-lg p-2">
        <div className="flex items-center gap-x-2">
          <CpuChipIcon className="size-4" />
          <p className="text-sm">Custom LLM</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center border rounded-lg">
      <div className="px-2 border-r flex gap-x-2 items-center min-h-10">
        <ModelIcon />
        <p className="text-sm">
          {GPTResponseModels[gptResponseModel] ?? "GPT 4.1 Nano"}
        </p>
        {isPreviewModel(gptResponseModel) && renderPreviewModel()}
        {isPremiumModel(gptResponseModel) && (
          <Badge color="amber">
            <FaCrown />
          </Badge>
        )}
        {isProModel(gptResponseModel) && (
          <Badge color="orange">
            <FaStar />
          </Badge>
        )}
      </div>
      {!locked && (
        <Button
          plain
          onClick={() => setOpen(true)}
        >
          <PencilIcon />
        </Button>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
      >
        <DialogTitle>
          <DialogTitleWithClose
            title="Select Model"
            onClose={() => {
              setOpen(false);
            }}
          />
        </DialogTitle>
        <DialogBody>
          <GPTResponseModelSettings
            modelSettings={settings}
            syncAssistant={syncAssistant}
            workspaceId={workspaceId}
            assistantId={assistantId}
            onSuccess={() => setOpen(false)}
            showPremiumOptions={showPremiumOptions}
            showIndiaDeployedModels={showIndiaDeployedModels}
          />
        </DialogBody>
      </Dialog>
    </div>
  );
}
